import { isSettingsNavItem, type ResolvedNavItem } from "~/components/nav-launcher";
import { Button } from "~/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { navIcon } from "~/constants/nav-catalog";
import { cn } from "~/lib/utils";
import { GripVertical, Plus, X } from "lucide-react";
import { useEffect, useMemo, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";

type NavBarEditorProps = {
  // Barra actual (verdad del servidor). El editor mantiene su propio orden local
  // sembrado desde aquí y lo re-sincroniza cuando cambia (persistencia optimista).
  bar: ResolvedNavItem[];
  // Catálogo completo de destinos permitidos (para el picker "Añadir").
  launcher: ResolvedNavItem[];
  // Se llama con la lista COMPLETA de ids visibles (en orden) tras reordenar,
  // quitar o añadir. El padre decide si persistir de inmediato o acumular.
  onChange: (desiredVisibleIds: string[]) => void;
  // Muestra la nota "Solo visible para quienes tengan permiso" en items gated
  // (settings-*/integraciones). Útil en la página de empresa; off por defecto.
  showGatedNotes?: boolean;
  className?: string;
};

// Destinos cuya visibilidad depende de un permiso/feature del miembro final: el
// admin puede incluirlos en la barra por defecto, pero cada persona solo los verá
// si su rol los permite. Nota puramente informativa en el picker.
const isGatedNavItem = (item: ResolvedNavItem): boolean =>
  isSettingsNavItem(item) || item.id === "integrations";

export function NavBarEditor({
  bar,
  launcher,
  onChange,
  showGatedNotes = false,
  className,
}: NavBarEditorProps) {
  const { t } = useTranslation(["home_common", "home_settings_setting"]);
  const [orderedIds, setOrderedIds] = useState<string[]>(() => bar.map((item) => item.id));
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    setOrderedIds(bar.map((item) => item.id));
  }, [bar]);

  // Índice por id sobre barra + launcher: cubre cualquier id visible aunque no
  // esté en el catálogo del launcher (defensivo) sin perder su ítem resuelto.
  const itemById = useMemo(() => {
    const map = new Map<string, ResolvedNavItem>();
    for (const item of [...launcher, ...bar]) {
      map.set(item.id, item);
    }
    return map;
  }, [launcher, bar]);

  const items = useMemo(
    () =>
      orderedIds
        .map((id) => itemById.get(id))
        .filter((item): item is ResolvedNavItem => item !== undefined),
    [orderedIds, itemById],
  );

  const addable = useMemo(
    () => launcher.filter((item) => !orderedIds.includes(item.id)),
    [launcher, orderedIds],
  );

  const labelOf = (item: ResolvedNavItem): string =>
    t(item.labelKey, {
      ns: isSettingsNavItem(item) ? "home_settings_setting" : "home_common",
      defaultValue: item.labelKey,
    });

  const addLabel = t("nav_editor.add", { ns: "home_common", defaultValue: "Añadir" });
  const removeLabel = t("nav_editor.remove", {
    ns: "home_common",
    defaultValue: "Quitar de la barra",
  });
  const dragLabel = t("nav_editor.drag", {
    ns: "home_common",
    defaultValue: "Arrastrar para reordenar",
  });
  const gatedNote = t("navigationBar.gatedNote", {
    ns: "home_settings_setting",
    defaultValue: "Solo visible para quienes tengan permiso",
  });

  const commit = (next: string[]): void => {
    setOrderedIds(next);
    onChange(next);
  };

  const reorder = (sourceId: string, targetId: string): void => {
    if (sourceId === targetId) {
      return;
    }
    const sourceIndex = orderedIds.indexOf(sourceId);
    const targetIndex = orderedIds.indexOf(targetId);
    if (sourceIndex < 0 || targetIndex < 0) {
      return;
    }
    const next = [...orderedIds];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    commit(next);
  };

  const removeItem = (id: string): void => {
    commit(orderedIds.filter((entry) => entry !== id));
  };

  const addItem = (id: string): void => {
    if (orderedIds.includes(id)) {
      return;
    }
    commit([...orderedIds, id]);
    setAddOpen(false);
  };

  const handleDragStart = (event: DragEvent<HTMLDivElement>, id: string): void => {
    setDraggingId(id);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", id);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>, id: string): void => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragOverId(id);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>, targetId: string): void => {
    event.preventDefault();
    const sourceId = event.dataTransfer.getData("text/plain") || draggingId;
    setDraggingId(null);
    setDragOverId(null);
    if (sourceId) {
      reorder(sourceId, targetId);
    }
  };

  const handleDragEnd = (): void => {
    setDraggingId(null);
    setDragOverId(null);
  };

  return (
    <div className={cn("flex min-w-0 flex-wrap items-center gap-1.5", className)}>
      {items.map((item) => {
        const Icon = navIcon(item.iconName);
        return (
          <div
            key={item.id}
            draggable
            onDragStart={(event) => handleDragStart(event, item.id)}
            onDragOver={(event) => handleDragOver(event, item.id)}
            onDragLeave={() => setDragOverId(null)}
            onDrop={(event) => handleDrop(event, item.id)}
            onDragEnd={handleDragEnd}
            className={cn(
              "bg-background flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-sm transition",
              draggingId === item.id && "opacity-50",
              dragOverId === item.id && "ring-primary ring-1",
            )}
          >
            <GripVertical
              className="text-muted-foreground size-3.5 shrink-0 cursor-grab"
              aria-label={dragLabel}
            />
            <Icon className="size-4 shrink-0" />
            <span className="min-w-0 truncate">{labelOf(item)}</span>
            <button
              type="button"
              aria-label={`${removeLabel}: ${labelOf(item)}`}
              title={removeLabel}
              className="text-muted-foreground hover:text-foreground shrink-0 rounded-sm p-0.5 transition"
              onClick={() => removeItem(item.id)}
            >
              <X className="size-3.5" />
            </button>
          </div>
        );
      })}

      <Popover open={addOpen} onOpenChange={setAddOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-dashed"
            disabled={addable.length === 0}
          >
            <Plus className="size-4" />
            {addLabel}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-1">
          {addable.length === 0 ? (
            <div className="text-muted-foreground px-2 py-3 text-center text-sm">
              {t("nav_editor.all_in_bar", {
                ns: "home_common",
                defaultValue: "Todas las apps ya están en la barra.",
              })}
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              {addable.map((item) => {
                const Icon = navIcon(item.iconName);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => addItem(item.id)}
                    className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm"
                  >
                    <Icon className="size-4 shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{labelOf(item)}</span>
                    {showGatedNotes && isGatedNavItem(item) && (
                      <span className="text-muted-foreground shrink-0 text-[10px]">
                        {gatedNote}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
